import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ChangeEvent } from 'react';
import { UpdateSelectionResponse } from 'src/AppState/sdk/staff';
import { TypeId } from 'src_common/common/websocket2/type';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';

interface SelectionUpdateModelType {
    active?: boolean | null;
    betLimits?: {
        liabilityLimit?: number | null;
        maxBet?: number | null;
        maxMultipleBet?: number | null;
        maxWin?: number | null;
    } | null;
    display?: boolean | null;
    displayOrder?: number | null;
    name?: string | null;
    selectionSettings?: {
        defaultSpPrice?: { type: string; value: string } | null;
    } | null;
    price?: string | null;
    priceType?: 'sp' | 'fp' | 'boosted' | null;
    feedSettings?: {
        eachWay?: boolean | null;
        prices?: boolean | null;
        results?: boolean | null;
        updates?: boolean | null;
    } | null;
    tags?: Record<string, Array<string>> | null;
}

export class SelectionsSettingsFormState {
    @observable private selectionNameInputValue: string | null = null;
    @observable private selectionPriceInputValue: string | null = null;
    @observable private boostedPriceInputValue: string | null = null;
    @observable private maxBetInputValue: string | null = null;
    @observable private maxMultipleBetInputValue: string | null = null;
    @observable private maxWinInputValue: string | null = null;
    @observable private liabilityLimitInputValue: string | null = null;
    @observable private defaultSpPriceInputValue: string | null = null;
    @observable public expanded: boolean = false;

    public constructor(
        private readonly appState: AppState,
        private readonly marketId: TypeId.MarketId,
        private readonly selectionId: number
    ) {
        makeAutoObservable(this);
    }

    private get selectionModel(): SelectionModel | null {
        return this.appState.sdkStaff.models.getSelectionAndLoad(this.marketId, this.selectionId);
    }

    private requireSelectionModel(): SelectionModel | null {
        const model = this.selectionModel;
        if (model === null) {
            console.warn('SelectionsSettings State - selectionModel is empty');
            return null;
        }
        return model;
    }

    @computed public get getSelectionNameValue(): string {
        if (this.selectionNameInputValue === null) {
            const selectionModel = this.appState.sdkStaff.models.getSelectionAndLoad(this.marketId, this.selectionId);
            return selectionModel?.name ?? '';
        }

        return this.selectionNameInputValue;
    }

    @action public toggleExpanded = (): void => {
        this.expanded = !this.expanded;
    };

    @action public onSelectionNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.selectionNameInputValue = event.target.value;
    };

    @action public onSaveSelectionName = async (): Promise<void> => {
        if (this.selectionNameInputValue === null || this.selectionNameInputValue.trim() === '') {
            this.selectionNameInputValue = null;
            return;
        }

        await this.updateSelection({ name: this.selectionNameInputValue });
        this.selectionNameInputValue = null;
    };

    @computed public get getSelectionPriceValue(): string {
        if (this.selectionPriceInputValue === null) {
            const selectionModel = this.appState.sdkStaff.models.getSelectionAndLoad(this.marketId, this.selectionId);
            return selectionModel?.price?.f ?? '';
        }

        return this.selectionPriceInputValue;
    }

    @action public onSelectionPriceChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.selectionPriceInputValue = event.target.value;
    };

    @action public onSelectionPriceSave = async (): Promise<void> => {
        if (
            this.selectionPriceInputValue !== '0' &&
            this.selectionPriceInputValue !== '0/0' &&
            this.selectionPriceInputValue?.trim() !== ''
        ) {
            await this.updateSelection({ price: this.selectionPriceInputValue ?? '', priceType: 'fp' });
        }

        this.selectionPriceInputValue = null;
    };

    @computed public get getBoostedPriceValue(): string {
        if (this.boostedPriceInputValue === null) {
            const selectionModel = this.appState.sdkStaff.models.getSelectionAndLoad(this.marketId, this.selectionId);
            return selectionModel?.boostedPrice?.f ?? '';
        }

        return this.boostedPriceInputValue;
    }

    @action public onBoostedPriceChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.boostedPriceInputValue = event.target.value;
    };

    @action public onBoostedPriceSave = async (): Promise<void> => {
        if (this.boostedPriceInputValue === null) {
            return;
        }

        const newValueRaw = this.boostedPriceInputValue;
        const newValue = newValueRaw.trim();

        if (newValue === '') {
            await this.updateSelection({
                price: null,
                priceType: 'boosted',
            });
            this.boostedPriceInputValue = null;
            return;
        }

        if (newValueRaw !== '0' && newValueRaw !== '0/0' && newValue !== '') {
            await this.updateSelection({
                price: newValueRaw,
                priceType: 'boosted',
            });
        }

        this.boostedPriceInputValue = null;
    };

    @action public onDisplayChange = async (): Promise<void> => {
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return;
        }
        await this.updateSelection({ display: !selectionModel.display });
    };

    @action public onActiveChange = async (): Promise<void> => {
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return;
        }
        await this.updateSelection({ active: !selectionModel.active });
    };

    @action public onFeedSettingsUpdatesChange = async (): Promise<void> => {
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return;
        }

        await this.updateSelection({
            feedSettings: {
                updates: !(selectionModel.feedSettings?.updates ?? false),
            },
        });
    };

    @action public onFeedSettingsPricesChange = async (): Promise<void> => {
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return;
        }
        await this.updateSelection({
            feedSettings: {
                prices: !(selectionModel.feedSettings?.prices ?? false),
            },
        });
    };

    @computed public get maxBetValue(): string {
        if (this.maxBetInputValue === null) {
            const selectionModel = this.requireSelectionModel();
            const maxBet = selectionModel?.betLimits?.maxBet;
            return maxBet !== undefined && maxBet !== null && maxBet !== 0 ? (maxBet / 100).toString() : '';
        }

        return this.maxBetInputValue;
    }

    @action public onMaxBetChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.maxBetInputValue = event.target.value;
    };

    @action public onMaxBetSave = async (): Promise<void> => {
        if (this.maxBetInputValue === null) {
            return;
        }

        const updateValue = isNaN(parseInt(this.maxBetInputValue, 10)) ? 0 : parseInt(this.maxBetInputValue, 10);
        await this.updateSelection({ betLimits: { maxBet: updateValue * 100 } });

        this.maxBetInputValue = null;
    };

    @computed public get maxMultipleBetValue(): string {
        if (this.maxMultipleBetInputValue === null) {
            const selectionModel = this.requireSelectionModel();

            const maxMultipleBet = selectionModel?.betLimits?.maxMultipleBet;
            return maxMultipleBet !== undefined && maxMultipleBet !== null && maxMultipleBet !== 0
                ? (maxMultipleBet / 100).toString()
                : '';
        }

        return this.maxMultipleBetInputValue;
    }

    @action public onMaxMultipleBetChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.maxMultipleBetInputValue = event.target.value;
    };

    @action public onMaxMultipleBetSave = async (): Promise<void> => {
        if (this.maxMultipleBetInputValue === null) {
            return;
        }

        const updateValue = isNaN(parseInt(this.maxMultipleBetInputValue, 10))
            ? 0
            : parseInt(this.maxMultipleBetInputValue, 10);
        await this.updateSelection({ betLimits: { maxMultipleBet: updateValue * 100 } });

        this.maxMultipleBetInputValue = null;
    };

    @computed public get maxWinValue(): string {
        if (this.maxWinInputValue === null) {
            const selectionModel = this.requireSelectionModel();
            const maxWin = selectionModel?.betLimits?.maxWin;
            return maxWin !== undefined && maxWin !== null && maxWin !== 0 ? (maxWin / 100).toString() : '';
        }

        return this.maxWinInputValue;
    }

    @action public onMaxWinChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.maxWinInputValue = event.target.value;
    };

    @action public onMaxWinSave = async (): Promise<void> => {
        if (this.maxWinInputValue === null) {
            return;
        }

        const updateValue = isNaN(parseInt(this.maxWinInputValue, 10)) ? 0 : parseInt(this.maxWinInputValue, 10);

        await this.updateSelection({ betLimits: { maxWin: updateValue * 100 } });

        this.maxWinInputValue = null;
    };

    @computed public get defaultSpPriceValue(): string {
        if (this.defaultSpPriceInputValue === null) {
            const selectionModel = this.requireSelectionModel();
            const defaultSpPrice = selectionModel?.selectionSettings?.defaultSpPrice;
            return defaultSpPrice ?? '';
        }

        return this.defaultSpPriceInputValue;
    }

    @action public onDefaultSpPriceChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.defaultSpPriceInputValue = event.target.value;
    };

    @action public onDefaultSpPriceSave = async (): Promise<void> => {
        if (this.defaultSpPriceInputValue === null) {
            return;
        }

        await this.updateSelection({
            selectionSettings: { defaultSpPrice: { type: 'fraction', value: this.defaultSpPriceInputValue } },
        });

        this.defaultSpPriceInputValue = null;
    };

    @computed public get liabilityLimitValue(): string {
        if (this.liabilityLimitInputValue === null) {
            const selectionModel = this.requireSelectionModel();
            const liabilityLimit = selectionModel?.betLimits?.liabilityLimit;
            return liabilityLimit !== undefined && liabilityLimit !== null && liabilityLimit !== 0
                ? (liabilityLimit / 100).toString()
                : '';
        }

        return this.liabilityLimitInputValue;
    }

    @action public onLiabilityLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
        this.liabilityLimitInputValue = event.target.value;
    };

    @action public onLiabilityLimitSave = async (): Promise<void> => {
        if (this.liabilityLimitInputValue === null) {
            return;
        }

        const updateValue = isNaN(parseInt(this.liabilityLimitInputValue, 10))
            ? 0
            : parseInt(this.liabilityLimitInputValue, 10);

        await this.updateSelection({ betLimits: { liabilityLimit: updateValue * 100 } });

        this.liabilityLimitInputValue = null;
    };

    @action public onTagChange = async (event: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const tagName = event.target.name;
        const tagNewValue = event.target.value;
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return;
        }
        const allTags = selectionModel.getAllTags();

        const tagsForUpdate: Record<string, Array<string>> = {};
        for (const tag in allTags) {
            const current = allTags[tag];
            if (current !== undefined) {
                tagsForUpdate[tag] = [current];
            }
        }

        tagsForUpdate[tagName] = [tagNewValue];

        await this.updateSelection({ tags: tagsForUpdate });
    };

    @action public updateSelection = async (
        dataIn: SelectionUpdateModelType
    ): Promise<UpdateSelectionResponse | null> => {
        const selectionModel = this.requireSelectionModel();
        if (selectionModel === null) {
            return null;
        }

        const feedSettings = dataIn.feedSettings !== undefined;
        const price = dataIn.price !== undefined;

        const requestBody = {
            active: undefined,
            betLimits: undefined,
            display: undefined,
            displayOrder: undefined,
            name: undefined,
            selectionSettings: undefined,
            tags: undefined,
        };

        const data =
            feedSettings === true
                ? {
                      ...dataIn.feedSettings,
                  }
                : dataIn;

        const response = await this.appState.sdkStaff.updateSelection(
            {
                event_id: selectionModel.eventId,
                market_id: TypeId.getMarketId(this.marketId),
                selection_id: this.selectionId,
                //@ts-expect-error -- to create no type
                requestBody: { ...requestBody, ...data },
            },
            feedSettings,
            price
        );

        if (response.status === 'error') {
            console.warn('Selection update error response - ', response);
        }

        return response;
    };

    @action public onQueryAudit = (): void => {
        this.appState.reduxAction.onQueryAudit(`trading_content_selection-change-${this.selectionId}`, {
            topics: ['trading_content'],
            tags: [`selection-change-${this.selectionId}`],
        });
        // this.props.onQueryAudits(`trading_content_selection-change-${props.selectionId}`, 'trading_content', `selection-change-${props.selectionId}`);
    };
}
